import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { voiceApi } from '../lib/api';

interface Message {
  id: string;
  role: 'user' | 'jarvis';
  content: string;
  timestamp: Date;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export default function JarvisChat() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'kyc' | 'voice'>('kyc');
  const [kycData, setKycData] = useState({ name: '', email: '', company: '' });
  const [messages, setMessages] = useState<Message[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [error, setError] = useState('');
  
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (pcRef.current) {
        pcRef.current.close();
      }
    };
  }, []);

  const addMessage = useCallback((role: 'user' | 'jarvis', content: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role,
        content,
        timestamp: new Date(),
      },
    ]);
  }, []);

  // WebRTC接続を開始
  const startVoiceSession = async () => {
    setConnectionStatus('connecting');
    setError('');

    try {
      // 1. バックエンドからエフェメラルトークンを取得
      const sessionData = await voiceApi.createSession(kycData.name);
      
      // 2. RTCPeerConnection を作成
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // 3. 音声出力用のaudio要素を設定
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioRef.current = audioEl;
      
      pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
      };

      // 4. マイク入力を取得して追加
      const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      pc.addTrack(ms.getTracks()[0]);

      // 5. DataChannel を作成
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.onopen = () => {
        console.log('DataChannel opened');
        setConnectionStatus('connected');
        setIsListening(true);
        
        // 初回の挨拶をトリガー
        dc.send(JSON.stringify({
          type: 'response.create',
          response: {
            modalities: ['text', 'audio'],
            instructions: `${kycData.name}様に挨拶してください。`,
          },
        }));
      };

      dc.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          handleRealtimeEvent(event);
        } catch (err) {
          console.error('Failed to parse event:', err);
        }
      };

      dc.onerror = (e) => {
        console.error('DataChannel error:', e);
        setConnectionStatus('error');
        setError('接続エラーが発生しました');
      };

      dc.onclose = () => {
        console.log('DataChannel closed');
        setConnectionStatus('disconnected');
        setIsListening(false);
      };

      // 6. SDP Offer を作成
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // 7. OpenAI Realtime API にオファーを送信
      const baseUrl = 'https://api.openai.com/v1/realtime';
      const model = 'gpt-4o-realtime-preview-2024-12-17';
      
      const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${sessionData.token}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (!sdpResponse.ok) {
        throw new Error('SDP交換に失敗しました');
      }

      const answerSdp = await sdpResponse.text();
      await pc.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });

    } catch (err) {
      console.error('Voice session error:', err);
      setConnectionStatus('error');
      setError(err instanceof Error ? err.message : '音声セッションの開始に失敗しました');
    }
  };

  // Realtime APIイベントを処理
  const handleRealtimeEvent = (event: Record<string, unknown>) => {
    const eventType = event.type as string;
    
    switch (eventType) {
      case 'conversation.item.input_audio_transcription.completed': {
        const transcript = (event as { transcript?: string }).transcript;
        if (transcript) {
          addMessage('user', transcript);
          setCurrentTranscript('');
        }
        break;
      }
      
      case 'response.audio_transcript.delta': {
        const delta = (event as { delta?: string }).delta;
        if (delta) {
          setCurrentTranscript((prev) => prev + delta);
        }
        break;
      }
      
      case 'response.audio_transcript.done': {
        const transcript = (event as { transcript?: string }).transcript;
        if (transcript) {
          addMessage('jarvis', transcript);
          setCurrentTranscript('');
        }
        break;
      }
      
      case 'response.audio.delta': {
        setIsSpeaking(true);
        break;
      }
      
      case 'response.audio.done': {
        setIsSpeaking(false);
        break;
      }
      
      case 'input_audio_buffer.speech_started': {
        setIsListening(true);
        break;
      }
      
      case 'input_audio_buffer.speech_stopped': {
        setIsListening(false);
        break;
      }
      
      case 'error': {
        const errorEvent = event as { error?: { message?: string } };
        console.error('Realtime error:', errorEvent.error);
        setError(errorEvent.error?.message || 'エラーが発生しました');
        break;
      }
    }
  };

  // セッション終了
  const endSession = () => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (dcRef.current) {
      dcRef.current.close();
      dcRef.current = null;
    }
    setConnectionStatus('disconnected');
    setIsListening(false);
    setIsSpeaking(false);
  };

  // KYC送信
  const handleKycSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kycData.name) {
      setError('お名前を入力してください');
      return;
    }
    setStep('voice');
    // 音声セッションを開始
    await startVoiceSession();
  };

  // KYC画面
  if (step === 'kyc') {
    return (
      <div className="h-[calc(100vh-7rem)] flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md bg-theme-card rounded-xl p-8 border border-theme transition-colors duration-300">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🤖</div>
              <h2 className="text-xl font-bold text-theme-primary">Jarvisへようこそ</h2>
              <p className="text-theme-secondary mt-2">音声でシステム開発の相談ができます</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-500 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleKycSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  お名前 *
                </label>
                <input
                  type="text"
                  value={kycData.name}
                  onChange={(e) => setKycData({ ...kycData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
                  placeholder="山田太郎"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  メールアドレス
                </label>
                <input
                  type="email"
                  value={kycData.email}
                  onChange={(e) => setKycData({ ...kycData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
                  placeholder="taro@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  会社名（任意）
                </label>
                <input
                  type="text"
                  value={kycData.company}
                  onChange={(e) => setKycData({ ...kycData, company: e.target.value })}
                  className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
                  placeholder="株式会社〇〇"
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full py-4 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  <span className="text-2xl">🎙️</span>
                  <span>音声対話を開始</span>
                </button>
                <p className="text-xs text-theme-secondary text-center mt-2">
                  マイクの使用許可が必要です
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // 音声会話画面
  return (
    <div className="h-[calc(100vh-7rem)] flex flex-col">
      {/* ヘッダー */}
      <div className="bg-theme-card border-b border-theme p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${
            connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' :
            connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
            connectionStatus === 'error' ? 'bg-red-500' :
            'bg-gray-500'
          }`} />
          <span className="text-theme-primary font-medium">
            {connectionStatus === 'connected' ? '接続中' :
             connectionStatus === 'connecting' ? '接続中...' :
             connectionStatus === 'error' ? 'エラー' :
             '切断'}
          </span>
        </div>
        <button
          onClick={() => {
            endSession();
            navigate('/sessions');
          }}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
        >
          終了
        </button>
      </div>

      {/* メッセージエリア */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] md:max-w-[60%] p-4 rounded-xl ${
                msg.role === 'user'
                  ? 'bg-accent text-navy-900'
                  : 'bg-theme-card border border-theme text-theme-primary'
              } transition-colors duration-300`}
            >
              {msg.role === 'jarvis' && (
                <div className="flex items-center gap-2 mb-2">
                  <span>🤖</span>
                  <span className="font-medium text-accent">Jarvis</span>
                </div>
              )}
              <p className="whitespace-pre-wrap">{msg.content}</p>
              <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-navy-700' : 'text-theme-secondary'}`}>
                {msg.timestamp.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}

        {/* 現在の発話中テキスト */}
        {currentTranscript && (
          <div className="flex justify-start">
            <div className="max-w-[80%] md:max-w-[60%] p-4 rounded-xl bg-theme-card border border-theme text-theme-primary">
              <div className="flex items-center gap-2 mb-2">
                <span>🤖</span>
                <span className="font-medium text-accent">Jarvis</span>
                <span className="text-xs text-theme-secondary">（発話中...）</span>
              </div>
              <p className="whitespace-pre-wrap">{currentTranscript}</p>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ステータスバー */}
      <div className="bg-theme-card border-t border-theme p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-500 text-sm text-center">
            {error}
            <button
              onClick={() => {
                setError('');
                startVoiceSession();
              }}
              className="ml-2 underline"
            >
              再接続
            </button>
          </div>
        )}

        <div className="flex flex-col items-center">
          {/* アニメーション付きの音声インジケーター */}
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 transition-all duration-300 ${
            isSpeaking ? 'bg-accent animate-pulse scale-110' :
            isListening ? 'bg-blue-500 animate-pulse' :
            connectionStatus === 'connected' ? 'bg-green-500' :
            'bg-gray-500'
          }`}>
            <span className="text-4xl">
              {isSpeaking ? '🔊' : isListening ? '🎤' : '🤖'}
            </span>
          </div>
          
          <p className="text-theme-secondary text-center">
            {isSpeaking ? 'Jarvisが話しています...' :
             isListening ? 'お話しください...' :
             connectionStatus === 'connected' ? '待機中' :
             connectionStatus === 'connecting' ? '接続中...' :
             '切断されました'}
          </p>
          
          {connectionStatus === 'connected' && (
            <p className="text-xs text-theme-muted mt-2">
              マイクに向かってお話しください
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
